import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isStaff = user && ["provider_admin", "platform_admin", "tutor"].includes(user.role);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = [
    { to: "/courses", label: "Courses" },
    { to: "/#how", label: "How it works" },
    { to: "/store", label: "Store" },
    { to: "/business-development", label: "Business Development" },
    { to: "/news", label: "News" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--line)] bg-white/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img src="/logo.png" alt="Get Skilled in Days Not Years" className="h-9 w-9 object-contain" />
          <span className="flex flex-col leading-none">
            <span className="font-display font-extrabold text-base tracking-tight">GET&nbsp;SKILLED</span>
            <span className="text-[9px] tracking-[0.12em] uppercase text-[var(--ink-2)] font-semibold">Days, not Years</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.to} href={l.to} className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors" data-testid={`nav-${l.label.split(" ")[0].toLowerCase()}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {isStaff && (
                <Link to="/provider" className="text-sm font-semibold hover:text-[var(--accent)]" data-testid="nav-provider">Dashboard</Link>
              )}
              {!isStaff && (
                <Link to="/dashboard" className="text-sm font-semibold hover:text-[var(--accent)]" data-testid="nav-dashboard">My learning</Link>
              )}
              <Link to="/account" className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--ink)]" data-testid="nav-account">Account</Link>
              <button onClick={handleLogout} className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--ink)]" data-testid="nav-logout">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-semibold" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="text-sm font-semibold bg-[var(--ink)] text-white px-4 py-2 hover:bg-[var(--accent)] transition-colors" data-testid="nav-register">
                Get started
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--line)] bg-white px-4 py-4 space-y-3" data-testid="nav-mobile-menu">
          <a href="/courses" className="block text-sm font-medium" onClick={() => setOpen(false)}>Courses</a>
          <a href="/store" className="block text-sm font-medium" onClick={() => setOpen(false)}>Store</a>
          <a href="/business-development" className="block text-sm font-medium" onClick={() => setOpen(false)}>Business Development</a>
          <a href="/news" className="block text-sm font-medium" onClick={() => setOpen(false)}>News</a>
          {user ? (
            <>
              <Link to={isStaff ? "/provider" : "/dashboard"} className="block text-sm font-semibold" onClick={() => setOpen(false)}>Dashboard</Link>
              <Link to="/account" className="block text-sm font-medium" onClick={() => setOpen(false)} data-testid="nav-account-mobile">Account</Link>
              <button onClick={handleLogout} className="block text-sm font-medium text-left">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-sm font-semibold" onClick={() => setOpen(false)}>Log in</Link>
              <Link to="/register" className="block text-sm font-semibold text-[var(--accent)]" onClick={() => setOpen(false)}>Get started</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
